import { existsSync, writeFileSync } from "fs";
import { join } from "path";
import type { FeatureInstaller, ProjectConfig } from "../../types/types.js";
import { logger } from "../../utils/logger.js";
import { PackageManagerAdapter } from "../../utils/package-manager.js";

export class BiomeInstaller implements FeatureInstaller {
  name = "Biome";

  async install(config: ProjectConfig): Promise<void> {
    logger.info("Setting up Biome for code quality...");

    if (config.dryRun) {
      logger.debug("[DRY RUN] Skipping Biome installation");
      return;
    }

    const pm = new PackageManagerAdapter(config.packageManager);

    pm.add(["@biomejs/biome"], true, config);
    this.createBiomeConfig(config);
    await this.updateScripts(config);

    logger.success("Biome configured successfully");
  }

  private createBiomeConfig(config: ProjectConfig): void {
    const biomeConfig = {
      $schema: "https://biomejs.dev/schemas/2.0.4/schema.json",
      assist: {
        actions: {
          source: {
            organizeImports: "on",
            useSortedAttributes: "on",
            useSortedKeys: "on",
          },
        },
        enabled: true,
      },
      css: {
        formatter: {
          enabled: true,
        },
        linter: {
          enabled: true,
        },
      },
      files: {
        includes: ["src/**", "!src/**/*.css", "*.ts"],
      },
      formatter: {
        indentStyle: "space",
        indentWidth: 2,
      },
      javascript: {
        formatter: {
          quoteStyle: "double",
          semicolons: "asNeeded",
        },
      },
      linter: {
        domains: {
          next: "recommended",
          react: "recommended",
          test: "all",
        },
        rules: {
          a11y: {
            noSvgWithoutTitle: "off",
            useAriaPropsSupportedByRole: "off",
          },
          complexity: {
            noExcessiveCognitiveComplexity: {
              level: "error",
              options: {
                maxAllowedComplexity: 22,
              },
            },
            noStaticOnlyClass: "off",
            noUselessFragments: "off",
            noUselessUndefinedInitialization: "error",
            useDateNow: "error",
          },
          correctness: {
            noNodejsModules: "off",
            noUndeclaredDependencies: "off",
            noUndeclaredVariables: "off",
            noUnusedFunctionParameters: "error",
            noUnusedImports: {
              fix: "safe",
              level: "error",
            },
            noUnusedVariables: "error",
            useExhaustiveDependencies: "error",
            useImportExtensions: "off",
          },
          nursery: {
            noNestedComponentDefinitions: "error",
            noSecrets: "off",
            useExplicitType: "off",
            useIterableCallbackReturn: "error",
            useSortedClasses: {
              fix: "safe",
              level: "on",
              options: {
                attributes: ["classList"],
                functions: ["clsx", "cva"],
              },
            },
          },
          performance: {
            noBarrelFile: "off",
            noDynamicNamespaceImportAccess: "error",
            noNamespaceImport: "off",
            useTopLevelRegex: "off",
          },
          security: {
            noBlankTarget: "error",
            noDangerouslySetInnerHtml: "off",
            noGlobalEval: "error",
          },
          style: {
            noDefaultExport: "off",
            noInferrableTypes: "error",
            noNamespace: "off",
            noNonNullAssertion: "warn",
            noParameterAssign: "error",
            noProcessEnv: "off",
            noRestrictedGlobals: {
              level: "error",
              options: {
                deniedGlobals: {
                  $: "Don't use $ — unless you're wiring it to my bank account",
                },
              },
            },
            noRestrictedImports: {
              level: "error",
              options: {
                paths: {
                  "next/router": "Use `next/navigation` instead",
                },
              },
            },
            noUnusedTemplateLiteral: "error",
            noUselessElse: "error",
            useAsConstAssertion: "error",
            useBlockStatements: "off",
            useComponentExportOnlyModules: "off",
            useConsistentArrayType: {
              level: "error",
              options: {
                syntax: "generic",
              },
            },
            useConsistentCurlyBraces: "off",
            useConst: "error",
            useDefaultParameterLast: "error",
            useDefaultSwitchClause: "off",
            useEnumInitializers: "error",
            useFilenamingConvention: {
              level: "error",
              options: {
                filenameCases: ["kebab-case", "PascalCase"],
              },
            },
            useNamingConvention: {
              level: "error",
              options: {
                conventions: [
                  {
                    formats: ["camelCase", "PascalCase"],
                    selector: {
                      kind: "variable",
                      scope: "any",
                    },
                  },
                  {
                    formats: ["snake_case", "PascalCase"],
                    selector: {
                      kind: "classProperty",
                      modifiers: ["readonly"],
                    },
                  },
                  {
                    formats: ["camelCase", "PascalCase", "CONSTANT_CASE"],
                    selector: {
                      kind: "typeProperty",
                      modifiers: ["readonly"],
                    },
                  },
                  {
                    formats: ["camelCase", "PascalCase"],
                    selector: {
                      kind: "typeParameter",
                    },
                  },
                  {
                    formats: [
                      "camelCase",
                      "PascalCase",
                      "CONSTANT_CASE",
                      "snake_case",
                    ],
                    selector: {
                      kind: "objectLiteralProperty",
                      scope: "any",
                    },
                  },
                ],
                strictCase: false,
              },
            },
            useNodejsImportProtocol: "off",
            useNumberNamespace: "error",
            useSelfClosingElements: "error",
            useSingleVarDeclarator: "error",
          },
          suspicious: {
            noArrayIndexKey: "off",
            noAssignInExpressions: "error",
            noConsole: "off",
            noDoubleEquals: "error",
            noEvolvingTypes: "off",
            noExplicitAny: "off",
            noImplicitAnyLet: "off",
            noMisplacedAssertion: "off",
            noReactSpecificProps: "off",
            noShadowRestrictedNames: "off",
            noVar: "error",
            useAwait: "error",
            useStrictMode: "off",
          },
        },
      },
      overrides: [
        {
          includes: ["**/src/**/*.component.*", "**/src/**/*.stories.*"],
          linter: {
            rules: {
              style: {
                useFilenamingConvention: {
                  level: "error",
                  options: {
                    filenameCases: ["PascalCase"],
                  },
                },
              },
            },
          },
        },
        {
          includes: ["**/src/**/*.spec.ts", "**/src/**/*.spec.tsx"],
          linter: {
            rules: {
              style: {
                useNamingConvention: "off",
              },
            },
          },
        },
        {
          includes: ["**/src/**/*.component.tsx"],
          linter: {
            rules: {
              style: {
                noDefaultExport: "on",
              },
            },
          },
        },
      ],
      root: true,
    };

    const configPath = join(config.projectPath, "biome.json");

    if (config.dryRun) {
      logger.debug("[DRY RUN] Would create Biome config");
      return;
    }

    writeFileSync(configPath, JSON.stringify(biomeConfig, null, 2));
    logger.debug("Created biome.json");
  }

  private async updateScripts(config: ProjectConfig): Promise<void> {
    if (config.dryRun) {
      logger.debug("[DRY RUN] Would update package.json scripts");
      return;
    }

    const packageJsonPath = join(config.projectPath, "package.json");
    const { readFileSync, writeFileSync } = await import("fs");

    const packageJson: Record<string, any> = existsSync(packageJsonPath)
      ? JSON.parse(readFileSync(packageJsonPath, "utf-8"))
      : { name: config.projectName, scripts: {} };

    packageJson.scripts = {
      ...packageJson.scripts,
      check: "pnpm check:biome && pnpm check:types",
      "check:biome": "biome check --error-on-warnings",
      "check:format": "biome check --write",
      "check:types": "tsc",
    };

    writeFileSync(packageJsonPath, JSON.stringify(packageJson, null, 2));
    logger.debug(
      `${existsSync(packageJsonPath) ? "Updated" : "Created"} package.json scripts`,
    );
  }
}
